import {VillageCalendar} from "./villageCalendar";
import {
  independentAndServicedApartmentResidents,
  independentResidents,
  ServicedApartmentResidentsFeatureName,
  villageFeatureKey
} from "../constants";
import {isFeatureToggleEnabled} from "../../utils/featureToggle/featureToggle";

export interface Village {
  /**The id of the village.*/
  id: string;
  /**The name of the village.*/
  name: string;
  /**The code of the village.*/
  code: string;
  /**The time zone of the village.*/
  timeZone: string;
  /**The country this village is in.*/
  country?: string;
  /**List of calendars that belongs to this village.*/
  calendars?: VillageCalendar[];
  /**
   * True if any of this village's calendars targets more than one person
   * type (i.e. a calendar shared across multiple person types, such as
   * "Independent and Serviced Apartment Residents"). Derived from
   * `calendars[].personTypeIds` via {@link calculateHasSharedCalendar}.
   */
  hasSharedCalendar?: boolean;
}

/**
 * Determines whether any of the given calendars is shared across more
 * than one person type (i.e. `personTypeIds` has more than 1 entry).
 */
export function calculateHasSharedCalendar(
  calendars?: VillageCalendar[]
): boolean {
  return (calendars ?? []).some(
    (calendar) => (calendar.personTypeIds?.length ?? 0) > 1
  );
}

/**
 * Determines whether a village has serviced apartment residents, driven
 * strictly by the per-village `VillageFeature_ServicedApartmentResidents_<villageId>`
 * feature toggle (the global `ServicedApartmentResidents` toggle is
 * intentionally ignored here). Used only by
 * {@link filterVillagesByServicedApartmentAudience}.
 */
function villageHasPerVillageServicedApartmentResidentsToggle(
  villageId: string
): boolean {
  return isFeatureToggleEnabled(
    `${villageFeatureKey}_${ServicedApartmentResidentsFeatureName}_${villageId}`
  );
}

/**
 * Scopes a list of villages to those that support the given Multi-Village
 * calendar audience, driven by the `ServicedApartmentResidents` feature
 * toggle (see {@link villageHasPerVillageServicedApartmentResidentsToggle}):
 * - "Independent Residents" targets residents without serviced apartments, so
 *   villages that have serviced apartment residents are excluded.
 * - "Independent and Serviced Apartment Residents" targets the combined
 *   audience, so only villages with serviced apartment residents are included.
 *
 * Any other (or no) calendar name is returned unfiltered.
 */
export function filterVillagesByServicedApartmentAudience<T extends Village>(
  villages: T[],
  calendarName?: string
): T[] {
  if (calendarName === independentResidents) {
    return villages.filter(
      (village) =>
        !villageHasPerVillageServicedApartmentResidentsToggle(village.id)
    );
  }
  if (calendarName === independentAndServicedApartmentResidents) {
    return villages.filter(
      (village) =>
        villageHasPerVillageServicedApartmentResidentsToggle(village.id)
    );
  }
  return villages;
}
